<script setup>
import { currentDayRange } from '../dateTime/historyDateTime.js'
import { useI18n } from '../i18n.js'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ArrowPathIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import Pts2ControllerSelector from '../components/pts2/Pts2ControllerSelector.vue'
import Pts2EntityBadge from '../components/pts2/Pts2EntityBadge.vue'
import Pts2HistoryExport from '../components/pts2/Pts2HistoryExport.vue'
import Pts2FilterChip from '../components/pts2/Pts2FilterChip.vue'
import Pts2TransactionFilters from '../components/pts2/Pts2TransactionFilters.vue'
import Pts2TransactionDrawer from '../components/pts2/Pts2TransactionDrawer.vue'
import { normalizePts2Context } from '../pts2/context.js'
import {
  getPts2TransactionFilterOptions,
  getPts2TransactionDetail,
  getPts2Transactions,
  paginationItems,
  transactionRangeIssue,
} from '../pts2/transactions.js'
import { choosePts2Controller, loadPts2HistoryContext, session } from '../session.js'
import { createFacetLifecycle, emptyFacetOptions, facetItemsWithSelection, retainFacetSelection } from '../pts2/filterOptions.js'
import { useHistorySession } from '../pts2/historySession.js'
import { historyScope } from '../pts2/historyState.js'
import '../pts2/controllers.css'

const { locale, t } = useI18n()

const context = computed(() => normalizePts2Context(session.pts2Context ?? {}))
const activeController = computed(() => context.value.controllers.find((item) => Number(item.id) === Number(context.value.activeControllerId)))
const page = ref(emptyPage())
const pageNumber = ref(1)
const pageSize = ref(25)
const searchDraft = ref('')
const filtersOpen = ref(false)
const loading = ref(true)
const refreshing = ref(false)
const switching = ref(false)
const error = ref('')
const filterError = ref('')
const searchError = ref(false)
const rangeError = computed(() => searchError.value ? '' : filterError.value)
const updatedAt = ref(null)
const options = reactive({ SHIFT: emptyOptions(), TAG: emptyOptions(), PUMP: emptyOptions(), NOZZLE: emptyOptions(), TANK: emptyOptions(), FUEL: emptyOptions() })
const applied = reactive(defaultFilters())
const draft = reactive(draftFrom(applied))
const selectedId = ref(null)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')
let listRequest
let detailRequest
let generation = 0, ready = false, disposed = false, optionTimer
const denied = ref(false)
const scope = () => historyScope(session)

const cachedStale = ref(false)
const history = useHistorySession('transactions', { applied, draft, pageNumber, pageSize, options, denied, searchDraft })
const unapplied = history.unapplied

const resultFrom = computed(() => page.value.totalItems ? (page.value.page - 1) * page.value.limit + 1 : 0)
const resultTo = computed(() => page.value.totalItems ? resultFrom.value + page.value.items.length - 1 : 0)
const pageButtons = computed(() => paginationItems(page.value.page, page.value.totalPages))
const activeFilterGroups = computed(() => Number(Boolean(applied.transactionNumber)) + Number(applied.pumpNumbers.length > 0)
  + Number(applied.nozzleNumbers.length > 0) + Number(applied.tankNumbers.length > 0)
  + Number(applied.fuelGradeIds.length > 0) + Number(applied.shiftMode !== 'ALL')
  + Number(applied.tagMode !== 'ALL'))
const hasConditions = computed(() => activeFilterGroups.value > 0)
const activeChips = computed(() => {
  const result = []
  if (applied.transactionNumber) result.push({ key: 'transactionNumber', label: t('pts2UiHistoryTransactionNumber', { number: applied.transactionNumber }) })
  for (const number of applied.pumpNumbers) result.push({ key: `pump:${number}`, appearance: 'neutral', label: t('pts2UiHistoryPumpNumber', { number }) })
  for (const number of applied.nozzleNumbers) result.push({ key: `nozzle:${number}`, appearance: 'neutral', label: t('pts2UiHistoryNozzleNumber', { number }) })
  for (const number of applied.tankNumbers) result.push({ key: `tank:${number}`, appearance: 'neutral', label: t('pts2UiHistoryTankNumber', { number }) })
  if (applied.shiftMode === 'NUMBER') result.push({ key: 'shift', label: t('pts2UiHistoryShiftNumber', { number: applied.shiftNumber }) })
  if (applied.shiftMode === 'UNASSIGNED') result.push({ key: 'shift', label: t('pts2UiHistoryNoShift') })
  if (applied.tagMode !== 'ALL') {
    const selected = options.TAG.items.find((item) => item.ref === applied.tagRef) || (options.TAG.selected?.ref === applied.tagRef ? options.TAG.selected : null)
    result.push({ key: 'tag', label: applied.tagMode === 'ABSENT' ? t('pts2UiHistoryNoTag')
      : applied.tagMode === 'UNAVAILABLE' ? t('pts2UiHistoryTagUnavailable') : selected?.tag?.name || selected?.tag?.maskedValue || t('pts2UiHistorySelectedTag') })
  }
  for (const id of applied.fuelGradeIds) {
    const fuel = facetItemsWithSelection(options.FUEL).find((item) => item.number === id)
    result.push({ key: `fuel:${id}`, entityId: id, appearance: 'entity', label: fuel?.name || t('pts2UiHistoryFuelNumber', { number: id }) })
  }
  return result
})

function emptyPage() { return { items: [], page: 1, limit: 25, totalItems: 0, totalPages: 0 } }
function emptyOptions() { return emptyFacetOptions() }
function initialRange() {
  return currentDayRange()
}
function defaultFilters() {
  return { ...initialRange(), transactionNumber: null, pumpNumbers: [], nozzleNumbers: [], tankNumbers: [],
    fuelGradeIds: [], shiftMode: 'ALL', shiftNumber: null, tagMode: 'ALL', tagRef: null }
}
function draftFrom(value) {
  return { ...value, pumpNumbers: [...value.pumpNumbers], nozzleNumbers: [...value.nozzleNumbers],
    tankNumbers: [...value.tankNumbers], fuelGradeIds: [...value.fuelGradeIds], shiftSelection: value.shiftMode === 'NUMBER' ? `NUMBER:${value.shiftNumber}` : value.shiftMode,
    tagSelection: value.tagMode === 'REF' ? `REF:${value.tagRef}` : value.tagMode }
}
function criteria(value = applied) {
  return { stationId: context.value.stationId, controllerId: context.value.activeControllerId, ...value,
    pumpNumbers: [...value.pumpNumbers], nozzleNumbers: [...value.nozzleNumbers],
    tankNumbers: [...value.tankNumbers], fuelGradeIds: [...value.fuelGradeIds] }
}

function optionCriteria() {
  return criteria()
}
let observedZone = null, optionZoneGeneration = 0
const optionScope = () => JSON.stringify([scope(), optionZoneGeneration])
function observeOptionsZone(items) {
  const zone = items.find((item) => item.stationTimeZone)?.stationTimeZone
  if (!zone) return
  if (observedZone && observedZone !== zone) {
    optionZoneGeneration++; facets.sync()
    if (filtersOpen.value && ready && !denied.value) facets.ensure()
  }
  observedZone = zone
}
const facets = createFacetLifecycle({ states: options, getCriteria: optionCriteria, getScope: optionScope,
  isValid: (value) => ready && !denied.value && !switching.value && Number(value.stationId) === Number(session.platformContext?.activeStationId) && facetsValid(value), load: getPts2TransactionFilterOptions,
  onDenied: loseAccess,
})

function facetsValid(value) { return !transactionRangeIssue(value.from, value.to) }
async function initialize() {
  ready = false; loading.value = true; error.value = ''
  try {
    const value = await loadPts2HistoryContext({ isCurrent: () => !disposed })
    if (!value || disposed) return
    if (!context.value.activeControllerId) { history.deny(); loading.value = false; return }
    const retained = history.restore()
    ready = true
    if (filtersOpen.value) facets.ensure()
    if (retained?.page) {
      page.value = retained.page; updatedAt.value = new Date(retained.fetchedAt).toISOString()
      cachedStale.value = !retained.fresh; loading.value = false
      if (!retained.fresh) await refresh()
    } else await refresh({ initial: true })
  } catch (cause) {
    if (disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess(cause)
    else { history.deny(); error.value = message(cause); loading.value = false }
  }
}

async function refresh({ resetPage = false, initial = false } = {}) {
  if (denied.value || !context.value.stationId || !context.value.activeControllerId) { page.value = emptyPage(); loading.value = false; return }
  if (!facetsValid(applied)) return
  if (resetPage) pageNumber.value = 1
  if (!history.matchesQuery()) { page.value = emptyPage(); cachedStale.value = false }
  const descriptor = history.query()
  listRequest?.abort(); listRequest = new AbortController()
  const request = listRequest, current = ++generation, requestScope = scope()
  if (initial || !page.value.items.length) loading.value = true
  refreshing.value = true; error.value = ''
  try {
    const currentCriteria = criteria()
    const result = await getPts2Transactions(currentCriteria, { page: pageNumber.value, limit: pageSize.value, signal: request.signal })
    if (request.signal.aborted || current !== generation || requestScope !== scope() || disposed) return
    observeOptionsZone(result.items)
    page.value = result
    if (result.totalPages && result.page > result.totalPages) { pageNumber.value = result.totalPages; return refresh() }
    if (!result.totalItems && result.page !== 1) { pageNumber.value = 1; return refresh() }
    updatedAt.value = new Date().toISOString()
    pageNumber.value = result.page
    history.saveResult({ ...descriptor, pageNumber: result.page }, result); cachedStale.value = false
  } catch (cause) {
    if (cause?.name !== 'AbortError' && current === generation && requestScope === scope() && !disposed) {
      if ([401, 403, 404].includes(cause?.status)) { loseAccess(cause); return }
      error.value = message(cause)
    }
  } finally {
    if (current === generation) { loading.value = false; refreshing.value = false }
  }
}

function loadOption(facet, cursor, force = false) {
  if (!ready || denied.value || switching.value) return
  return facets.load(facet, cursor === undefined ? options[facet].requestedCursor : cursor, { force })
}
function reloadOptions() { if (ready && !denied.value) facets.refresh() }
async function selectController(id) {
  clearTimeout(optionTimer); clearOptions(); switching.value = true; error.value = ''; closeDrawer()
  try {
    const value = await choosePts2Controller(id)
    if (!value || disposed) return
    clearOptions()
    Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied)); searchDraft.value = ''
    await refresh({ resetPage: true, initial: true })
  } catch (cause) { error.value = message(cause) }
  finally { switching.value = false; if (filtersOpen.value && !denied.value) facets.ensure() }
}

async function applySearch() {
  searchError.value = false
  const value = searchDraft.value.trim()
  if (value && (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 65535)) {
    searchError.value = true; filterError.value = t('pts2UiHistorySearchInvalid'); return
  }
  applied.transactionNumber = value ? Number(value) : null; draft.transactionNumber = applied.transactionNumber; filterError.value = ''; await refresh({ resetPage: true })
}

async function applyFilters() {
  searchError.value = false
  const issue = transactionRangeIssue(draft.from, draft.to)
  if (issue === 'REQUIRED') { filterError.value = t('pts2UiHistoryRangeRequired'); return }
  if (issue === 'ORDER') { filterError.value = t('pts2UiHistoryRangeOrder'); return }
  if (issue === 'TOO_LONG') { filterError.value = t('pts2UiHistoryRangeTooLong'); return }
  const [shiftMode, shiftNumber] = draft.shiftSelection.split(':')
  const [tagMode, tagRef] = draft.tagSelection.split(':')
  Object.assign(applied, { from: draft.from, to: draft.to,
    transactionNumber: draft.transactionNumber,
    pumpNumbers: [...draft.pumpNumbers], nozzleNumbers: [...draft.nozzleNumbers], tankNumbers: [...draft.tankNumbers],
    fuelGradeIds: [...draft.fuelGradeIds], shiftMode, shiftNumber: shiftMode === 'NUMBER' ? Number(shiftNumber) : null,
    tagMode, tagRef: tagMode === 'REF' ? tagRef : null })
  filtersOpen.value = false; filterError.value = ''; closeDrawer()
  searchDraft.value = applied.transactionNumber ? String(applied.transactionNumber) : ''
  await Promise.all([refresh({ resetPage: true }), facets.ensure()])
}

async function clearFilters() {
  Object.assign(draft, draftFrom(defaultFilters())); filterError.value = ''; filtersOpen.value = true
}

async function removeChip(key) {
  if (key.startsWith('pump:')) draft.pumpNumbers = draft.pumpNumbers.filter((id) => id !== Number(key.slice(5)))
  else if (key.startsWith('nozzle:')) draft.nozzleNumbers = draft.nozzleNumbers.filter((id) => id !== Number(key.slice(7)))
  else if (key.startsWith('tank:')) draft.tankNumbers = draft.tankNumbers.filter((id) => id !== Number(key.slice(5)))
  else if (key.startsWith('fuel:')) draft.fuelGradeIds = draft.fuelGradeIds.filter((id) => id !== Number(key.slice(5)))
  else if (key === 'shift') Object.assign(draft, { shiftMode: 'ALL', shiftNumber: null, shiftSelection: 'ALL' })
  else if (key === 'tag') Object.assign(draft, { tagMode: 'ALL', tagRef: null, tagSelection: 'ALL' })
  else draft[key] = null
  filterError.value = ''; filtersOpen.value = true
}

async function goToPage(value) { pageNumber.value = value; closeDrawer(); await refresh() }
async function changePageSize(value) { pageSize.value = Number(value); await refresh({ resetPage: true }) }
async function openTransaction(item) {
  selectedId.value = item.id; detail.value = null; detailError.value = ''; detailLoading.value = true
  detailRequest?.abort()
  const request = new AbortController()
  detailRequest = request
  const requestScope = scope()
  try {
    const value = await getPts2TransactionDetail(context.value.stationId, context.value.activeControllerId, item.id, { signal: request.signal })
    if (!request.signal.aborted && detailRequest === request && selectedId.value === item.id && requestScope === scope() && !disposed) detail.value = value
  } catch (cause) {
    if (cause?.name === 'AbortError' || detailRequest !== request || selectedId.value !== item.id || requestScope !== scope() || disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess(cause)
    else detailError.value = message(cause)
  }
  finally { if (detailRequest === request && !request.signal.aborted) detailLoading.value = false }
}
function closeDrawer() { detailRequest?.abort(); selectedId.value = null; detail.value = null; detailError.value = ''; detailLoading.value = false }
function clearOptions() { clearTimeout(optionTimer); facets.clear() }
function loseAccess(cause) {
  history.deny(); cachedStale.value = false
  denied.value = true; generation++; listRequest?.abort(); clearOptions(); closeDrawer(); page.value = emptyPage()
  Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied))
  error.value = message(cause); loading.value = false; refreshing.value = false
}

function message(error) { return [401, 403, 404].includes(error?.status) ? t('pts2UiHistoryAccessDenied') : t('pts2UiHistoryLoadTransactionsError') }
function date(value, timeZone) {
  if (!value) return '—'
  const format = { dateStyle: 'short', timeStyle: 'medium' }
  if (timeZone) format.timeZone = timeZone
  return new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', format).format(new Date(value))
}
function number(value, digits = 3) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value); return Number.isFinite(parsed) ? parsed.toLocaleString(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : value
}

watch(scope, () => {
  history.resetOwner(); cachedStale.value = false; filtersOpen.value = false
  observedZone = null
  generation++; listRequest?.abort(); clearTimeout(optionTimer); clearOptions(); closeDrawer(); page.value = emptyPage()
  updatedAt.value = null; loading.value = false; refreshing.value = false; denied.value = false
  Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied)); searchDraft.value = ''
  if (ready && !switching.value && session.user && context.value.activeControllerId) { if (filtersOpen.value) facets.ensure(); refresh({ resetPage: true, initial: true }) }
}, { flush: 'sync' })
watch(optionCriteria, () => {
  clearTimeout(optionTimer)
  retainFacetSelection(options.TANK, draft.tankNumbers); retainFacetSelection(options.FUEL, draft.fuelGradeIds)
  retainFacetSelection(options.PUMP, draft.pumpNumbers); retainFacetSelection(options.NOZZLE, draft.nozzleNumbers)
  facets.sync()
  if (ready && filtersOpen.value && !switching.value && !denied.value) optionTimer = setTimeout(() => facets.ensure(), 150)
}, { deep: true })
watch(draft, () => {
  filterError.value = ''
  retainFacetSelection(options.TANK, draft.tankNumbers); retainFacetSelection(options.FUEL, draft.fuelGradeIds)
  retainFacetSelection(options.PUMP, draft.pumpNumbers); retainFacetSelection(options.NOZZLE, draft.nozzleNumbers)
}, { deep: true })
watch(filtersOpen, (open) => { if (open && ready && !switching.value && !denied.value) facets.ensure() })
onMounted(initialize)
onBeforeUnmount(() => { history.capture(); disposed = true; ready = false; generation++; clearTimeout(optionTimer); facets.dispose(); listRequest?.abort(); detailRequest?.abort() })
</script>

<template>
  <section class="pc-page pts2-transactions-page">
    <header class="pts2-transactions-heading"><div><h1>{{ t('pts2UiHistoryTransactionsTitle') }}</h1><p>{{ t('pts2UiHistoryTransactionsHint') }}</p></div>
      <span v-if="updatedAt">{{ t('pts2UiHistoryUpdated', { time: date(updatedAt).split(', ').at(-1) }) }}</span></header>
    <div class="pts2-transactions-context">
      <Pts2ControllerSelector :controllers="context.controllers" :active-controller-id="context.activeControllerId"
        :disabled="loading || switching" @select="selectController" />
      <button class="pc-secondary" type="button" :disabled="refreshing || switching || denied || !context.activeControllerId" @click="refresh(); reloadOptions()">
        <ArrowPathIcon :class="{ spinning: refreshing }" aria-hidden="true" />{{ refreshing ? t('pts2UiHistoryRefreshing') : t('pts2UiHistoryRefresh') }}
      </button>
    </div>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-if="unapplied" class="notice" role="status">{{ t('pts2UiHistoryUnappliedChanges') }}</p>
    <p v-if="cachedStale" class="notice" role="status">{{ t('pts2UiHistoryRetainedStale') }}</p>
    <section v-if="!loading && !context.controllers.length" class="pts2-empty-state"><strong>{{ t('pts2UiNoStationControllers') }}</strong></section>
    <section v-else-if="!loading && context.selectionRequired && !context.activeControllerId" class="pts2-empty-state"><strong>{{ t('pts2UiChooseController') }}</strong><p>{{ t('pts2UiHistoryTransactionsIsolation') }}</p></section>
    <section v-else class="pts2-transaction-workspace">
      <div class="pts2-transaction-toolbar">
        <form role="search" @submit.prevent="applySearch"><label for="pts2-transaction-search">{{ t('pts2UiHistorySearch') }}</label><div>
          <MagnifyingGlassIcon aria-hidden="true" /><input id="pts2-transaction-search" v-model="searchDraft" inputmode="numeric" autocomplete="off" :placeholder="t('pts2UiHistorySearchPlaceholder')">
          <button class="pc-primary" type="submit" :disabled="loading">{{ t('pts2UiHistoryFind') }}</button></div></form>
        <Pts2HistoryExport journal="transactions" :filters="criteria()" :scope="JSON.stringify([scope(), switching, denied])" :disabled="loading || refreshing || switching || denied || !context.activeControllerId">
          <button class="pc-secondary pts2-filter-toggle" type="button" :aria-expanded="filtersOpen" aria-controls="pts2-filter-panel" @click="filtersOpen = !filtersOpen">
            <FunnelIcon aria-hidden="true" />{{ t('pts2UiHistoryFilters') }} <span v-if="activeFilterGroups">{{ activeFilterGroups }}</span>
          </button>
        </Pts2HistoryExport>
      </div>
      <p v-if="filterError && (!filtersOpen || !rangeError)" class="field-error pts2-filter-error" role="alert">{{ filterError }}</p>
      <section v-if="filtersOpen" id="pts2-filter-panel"><header><h2>{{ t('pts2UiHistoryFilterTransactions') }}</h2><button type="button" :aria-label="t('pts2UiHistoryCloseFilters')" @click="filtersOpen = false">×</button></header>
        <Pts2TransactionFilters :model-value="draft" :options="options" :loading="switching || denied" :range-error="rangeError"
          @update:model-value="Object.assign(draft, $event)" @apply="applyFilters" @clear="clearFilters" @close="filtersOpen = false" @options="loadOption" @search="facets.search" @select="facets.select" />
      </section>
      <div v-if="activeChips.length" class="pts2-active-filter-chips" aria-live="polite">
        <Pts2FilterChip v-for="chip in activeChips" :key="chip.key" :entity-id="chip.entityId"
          :appearance="chip.appearance || 'neutral'" :label="chip.label" @remove="removeChip(chip.key)" />
        <button type="button" @click="clearFilters">{{ t('pts2UiHistoryClear') }}</button>
      </div>
      <section class="pts2-transaction-table-card"><header><h2>{{ t('pts2UiHistoryTransactions') }}</h2><span>{{ t('pts2UiHistoryShown', { count: page.items.length, total: page.totalItems }) }}</span></header>
        <p v-if="loading" class="pts2-drawer-state" role="status">{{ t('pts2UiHistoryLoadingTransactions') }}</p>
        <div v-else-if="page.items.length" class="pts2-table-scroll"><table><thead><tr>
          <th>{{ t('pts2UiHistoryCompletionTime') }}</th><th>{{ t('pts2UiHistoryTransactionNumberLabel') }}</th><th>{{ t('pts2UiHistoryShiftNumberLabel') }}</th><th>{{ t('pts2UiHistoryPumpNozzle') }}</th><th>{{ t('pts2UiHistoryFuel') }}</th><th>{{ t('pts2UiHistoryVolumeLiters') }}</th><th>{{ t('pts2UiHistoryPricePerLiter') }}</th><th>{{ t('pts2UiHistoryAmount') }}</th><th :aria-label="t('pts2UiHistoryDetails')"></th>
        </tr></thead><tbody><tr v-for="item in page.items" :key="item.id" :class="{ selected: selectedId === item.id }" @click="openTransaction(item)">
          <td :data-label="t('pts2UiHistoryCompletionTime')">{{ date(item.eventAt || item.receivedAt, item.stationTimeZone) }}</td><td :data-label="t('pts2UiHistoryTransactionNumberLabel')"><strong>{{ item.transactionNumber ?? '—' }}</strong></td><td :data-label="t('pts2UiHistoryShiftNumberLabel')">{{ item.shiftNumber ?? t('pts2UiHistoryNoShift') }}</td>
          <td :data-label="t('pts2UiHistoryPumpNozzle')">{{ item.pumpNumber ?? '—' }} / {{ item.nozzleNumber ?? '—' }}</td><td :data-label="t('pts2UiHistoryFuel')"><Pts2EntityBadge :entity-id="item.fuelGradeId" :label="item.fuelGradeName || '—'" /></td>
          <td :data-label="t('pts2UiHistoryVolumeLiters')">{{ number(item.volume) }}</td><td :data-label="t('pts2UiHistoryPricePerLiter')">{{ number(item.price, 2) }}</td><td :data-label="t('pts2UiHistoryAmount')">{{ number(item.amount, 2) }}</td><td><button class="pts2-row-detail" type="button"
            :aria-label="t('pts2UiHistoryOpenTransaction', { number: item.transactionNumber ?? '—' })" @click.stop="openTransaction(item)">›</button></td>
        </tr></tbody></table></div>
        <div v-else class="pts2-empty-state"><strong>{{ hasConditions ? t('pts2UiHistoryTransactionsFilteredEmpty') : t('pts2UiHistoryTransactionsEmpty') }}</strong><button v-if="hasConditions" class="pc-secondary" type="button" @click="clearFilters">{{ t('pts2UiHistoryClearFilters') }}</button></div>
        <footer v-if="page.totalItems" class="pts2-transaction-pagination"><label>{{ t('pts2UiHistoryRowsPerPage') }} <select :value="pageSize" :disabled="refreshing" @change="changePageSize($event.target.value)"><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option></select></label>
          <span>{{ t('pts2UiHistoryPageRange', { from: resultFrom, to: resultTo, total: page.totalItems }) }}</span><nav :aria-label="t('pts2UiHistoryTransactionPagination')"><button type="button" :aria-label="t('pts2UiHistoryPrevious')" :disabled="page.page <= 1 || refreshing" @click="goToPage(page.page - 1)">‹</button>
            <template v-for="button in pageButtons" :key="button"><span v-if="typeof button === 'string'">…</span><button v-else type="button" :class="{ active: button === page.page }" :aria-current="button === page.page ? 'page' : undefined" :disabled="refreshing" @click="goToPage(button)">{{ button }}</button></template>
            <button type="button" :aria-label="t('pts2UiHistoryNext')" :disabled="page.page >= page.totalPages || refreshing" @click="goToPage(page.page + 1)">›</button></nav>
        </footer>
      </section>
    </section>
    <Pts2TransactionDrawer v-if="selectedId" :detail="detail" :loading="detailLoading" :error="detailError" @close="closeDrawer" />
  </section>
</template>
